import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import MaskedInput, { conformToMask } from 'react-text-mask';
import isEmpty from 'lodash.isempty';
import cn from 'classnames';
import useAxios from 'axios-hooks';
import { isBefore, startOfDay, subYears } from 'date-fns';

import states from '../../../../models/states.json';
import {
	Button,
	Checkbox,
	SplitButton,
	Select,
	SelectRaw,
} from '../../../../components';
import { PHONE_MASK } from '../../../../config/constants';
import { isMobilePhoneInvalid } from '../../../../lib/util';

const formattedStates = states.map(({ name, abbreviation }) => ({
	value: abbreviation,
	label: name,
}));

const maskedNumber = (phoneNumber) => conformToMask(phoneNumber, PHONE_MASK);

const CampaignPatientModal = ({
	isOpen,
	onClose,
	onAdd,
	isAdding,
	onVerifyMobileNumber,
	phoneData = {},
	userId,
	campaignPatient,
}) => {
	const [first_name, setFirstName] = useState('');
	const [middle_name, setMiddleName] = useState('');
	const [last_name, setLastName] = useState('');
	const [suffix, setSuffix] = useState('');
	const [birth_dt, setBirthDt] = useState('');
	const [gender, setGender] = useState('F');
	const [address1, setAddress1] = useState('');
	const [address2, setAddress2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('AL');
	const [zip, setZip] = useState('');
	const [mobile_phone, setMobilePhone] = useState('');
	const [email_address, setEmailAddress] = useState('');
	const [selectedCampaigns, setSelectedCampaigns] = useState({});

	const [isPhoneVerified, setPhoneVerified] = useState(false);
	const [
		{ data: existingQuestionnaires, loading /* error */ },
		getExistingDemographicData,
	] = useAxios(
		{
			method: 'GET',
			url: `/providers/${userId}/campaign-demographic-questionnaire`,
		},
		{ manual: true }
	);
	const [{ data: campaigns /* error */ }] = useAxios({
		method: 'GET',
		url: `/providers/${userId}/campaign-questionnaire-list`,
		cache: {
			maxAge: 15 * 60 * 1000,
		},
	});

	useEffect(() => {
		if (isOpen) {
			if (campaignPatient) {
				setFirstName(
					isEmpty(campaignPatient.first_name) ? '' : campaignPatient.first_name
				);
				setMiddleName(
					isEmpty(campaignPatient.middle_name)
						? ''
						: campaignPatient.middle_name
				);
				setLastName(
					isEmpty(campaignPatient.last_name) ? '' : campaignPatient.last_name
				);
				setSuffix(
					isEmpty(campaignPatient.suffix) ? '' : campaignPatient.suffix
				);
				setGender(
					isEmpty(campaignPatient.gender) ? '' : campaignPatient.gender
				);
				setAddress1(
					isEmpty(campaignPatient.address1) ? '' : campaignPatient.address1
				);
				setAddress2(
					isEmpty(campaignPatient.address2) ? '' : campaignPatient.address2
				);
				setCity(isEmpty(campaignPatient.city) ? '' : campaignPatient.city);
				setState(isEmpty(campaignPatient.state) ? '' : campaignPatient.state);
				setZip(isEmpty(campaignPatient.zip) ? '' : campaignPatient.zip);
				setEmailAddress(
					isEmpty(campaignPatient.email) ? '' : campaignPatient.email
				);
				setMobilePhone(
					isEmpty(campaignPatient.phone_mobile)
						? ''
						: campaignPatient.phone_mobile
				);

				if (!isEmpty(campaignPatient.birth_dt)) {
					// 1940-03-15T00:00:00.000Z
					const [date] = campaignPatient.birth_dt.split('T');
					setBirthDt(date);
				}

				const formattedNumber = maskedNumber(
					campaignPatient.phone_mobile || ''
				).conformedValue;
				if (
					!isEmpty(campaignPatient.phone_mobile) &&
					formattedNumber.length === PHONE_MASK.length &&
					!isMobilePhoneInvalid(formattedNumber)
				) {
					onVerifyMobileNumber(campaignPatient.phone_mobile);
				}
				if (campaignPatient.campaign_demographic_id) {
					getExistingDemographicData({
						params: {
							campaign_demographic_id: campaignPatient.campaign_demographic_id,
						},
					});
				}
			} else {
				setFirstName('');
				setMiddleName('');
				setLastName('');
				setSuffix('');
				setBirthDt('');
				setGender('F');
				setAddress1('');
				setAddress2('');
				setCity('');
				setState('AL');
				setZip('');
				setMobilePhone('');
				setEmailAddress('');
			}
			setPhoneVerified(false);
			setSelectedCampaigns(
				campaigns.reduce((prev, next) => {
					return {
						...prev,
						[`${next.care_quest_group_id}`]: false,
					};
				}, {})
			);
		}
	}, [isOpen, campaignPatient]);

	useEffect(() => {
		if (!isPhoneVerified) {
			const formattedNumber = maskedNumber(mobile_phone).conformedValue;
			if (isMobilePhoneInvalid(formattedNumber)) {
				setPhoneVerified(false);
			} else if (
				!isEmpty(mobile_phone) &&
				!isMobilePhoneInvalid(formattedNumber) &&
				!isEmpty(phoneData)
			) {
				setPhoneVerified(true);
			}
		}
	}, [mobile_phone, phoneData, isPhoneVerified]);

	useEffect(() => {
		if (Array.isArray(existingQuestionnaires)) {
			const selectedCampaigns = existingQuestionnaires.reduce(
				(prev, { questionnaire_id }) => {
					prev[`${questionnaire_id}`] = true;
					return prev;
				},
				{}
			);
			setSelectedCampaigns(selectedCampaigns);
		}
	}, [existingQuestionnaires]);

	const handleVerifyMobile = () => {
		setPhoneVerified(false);
		const formattedNumber = maskedNumber(mobile_phone).conformedValue;
		if (
			!isEmpty(mobile_phone) &&
			formattedNumber.length === PHONE_MASK.length &&
			!isMobilePhoneInvalid(formattedNumber)
		) {
			onVerifyMobileNumber(mobile_phone);
		}
	};

	const handleCampaignToggled = ({ target }) => {
		setSelectedCampaigns({
			...selectedCampaigns,
			[`${target.value}`]: target.checked,
		});
	};

	const cmpgns = Object.keys(selectedCampaigns).reduce((prev, curr) => {
		if (selectedCampaigns[curr]) {
			prev.push(parseInt(curr, 10));
		}
		return prev;
	}, []);

	const handleAddPatientToCampaign = (
		send_campaign = false,
		sendToDocument = false
	) => {
		const data = {
			first_name,
			middle_name,
			last_name,
			suffix,
			birth_dt,
			gender,
			address1,
			address2,
			city,
			state,
			zip,
			phone_mobile: mobile_phone,
			email: email_address,
			// pcp_npi: adt.provider_npi,
			// pcp_first_name: adt.provider_first_name,
			// pcp_last_name: adt.provider_last_name,
			questionnaires: cmpgns,
			send_campaign,
		};
		if (campaignPatient && campaignPatient.campaign_demographic_id) {
			data.campaign_demographic_id = campaignPatient.campaign_demographic_id;
		}
		if (campaignPatient?.acrs_patient_id) {
			data.acrs_patient_id = campaignPatient.acrs_patient_id;
		}
		onAdd(data, sendToDocument);
	};

	let isOldEnough = true;
	if (!isEmpty(birth_dt)) {
		isOldEnough = isBefore(
			startOfDay(new Date(birth_dt)),
			subYears(new Date(), 18)
		);
	}
	const isValid =
		isOldEnough &&
		!isEmpty(first_name) &&
		!isEmpty(last_name) &&
		!isEmpty(address1) &&
		!isEmpty(city) &&
		!isEmpty(state) &&
		!isEmpty(zip) &&
		!isEmpty(gender) &&
		!isEmpty(birth_dt) &&
		((isPhoneVerified && phoneData.isTAVCapable) || !isEmpty(email_address)) &&
		cmpgns.length > 0;
	const isDocValid =
		isOldEnough &&
		!isEmpty(first_name) &&
		!isEmpty(last_name) &&
		!isEmpty(address1) &&
		!isEmpty(city) &&
		!isEmpty(state) &&
		!isEmpty(zip) &&
		!isEmpty(gender) &&
		!isEmpty(birth_dt) &&
		cmpgns.length > 0;

	const sendActions = [
		{
			title:
				campaignPatient && campaignPatient.campaign_demographic_id
					? 'Update without Send'
					: 'Add without Send',
			onClick: () => handleAddPatientToCampaign(false),
		},
	];
	// if (!campaignPatient) {
	// 	sendActions.push({
	// 		title: 'Add and Document',
	// 		onClick: () => handleAddPatientToCampaign(false, true)
	// 	});
	// }

	return (
		<Modal
			isOpen={isOpen}
			contentLabel="Add To Campaign"
			className="modal-dialog modal-md add-to-campaign-modal"
		>
			<div className="modal-content">
				<div className="modal-header">
					<div className="clearfix">
						<div className="pull-right">
							<button className="btn btn-link" onClick={onClose}>
								<em className="fa fa-lg fa-times" />
							</button>
						</div>
						<h3 className="modal-title">Add Patient to Campaigns</h3>
					</div>
				</div>
				<div className="modal-body form">
					<p>
						<span className="text-danger">*</span> Required field
					</p>
					{!isOldEnough && (
						<div className="text-danger">
							Patient must be 18 years of age or older
						</div>
					)}
					<div className="row">
						<div className="col-md-4">
							<div className="form-group">
								<label>
									First Name <span className="text-danger">*</span>
								</label>
								<input
									type="text"
									className="form-control"
									value={first_name}
									onChange={({ target }) => setFirstName(target.value)}
								/>
							</div>
						</div>
						<div className="col-md-2">
							<div className="form-group">
								<label>M. I.</label>
								<input
									type="text"
									className="form-control"
									value={middle_name}
									onChange={({ target }) => setMiddleName(target.value)}
								/>
							</div>
						</div>
						<div className="col-md-4">
							<div className="form-group">
								<label>
									Last Name <span className="text-danger">*</span>
								</label>
								<input
									type="text"
									className="form-control"
									value={last_name}
									onChange={({ target }) => setLastName(target.value)}
								/>
							</div>
						</div>
						<div className="col-md-2">
							<div className="form-group">
								<label>Suffix</label>
								<input
									type="text"
									className="form-control"
									value={suffix}
									onChange={({ target }) => setSuffix(target.value)}
								/>
							</div>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<div className="form-group">
								<label>
									Address, Line 1 <span className="text-danger">*</span>
								</label>
								<input
									type="text"
									className="form-control"
									value={address1}
									onChange={({ target }) => setAddress1(target.value)}
								/>
							</div>
						</div>
						<div className="col-md-6">
							<div className="form-group">
								<label>Address, Line 2</label>
								<input
									type="text"
									className="form-control"
									value={address2}
									onChange={({ target }) => setAddress2(target.value)}
								/>
							</div>
						</div>
					</div>
					<div className="row">
						<div className="col-md-5">
							<div className="form-group">
								<label>
									City <span className="text-danger">*</span>
								</label>
								<input
									type="text"
									className="form-control"
									value={city}
									onChange={({ target }) => setCity(target.value)}
								/>
							</div>
						</div>
						<div className="col-md-3">
							<div className="form-group">
								<label>
									State <span className="text-danger">*</span>
								</label>
								<div className="form-group">
									<SelectRaw
										options={formattedStates}
										selectedValue={state}
										onChange={({ target }) => setState(target.value)}
									/>
								</div>
							</div>
						</div>
						<div className="col-md-4">
							<div className="form-group">
								<label>
									Zip <span className="text-danger">*</span>
								</label>
								<input
									type="text"
									className="form-control"
									value={zip}
									onChange={({ target }) => setZip(target.value)}
								/>
							</div>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<div className="form-group">
								<label>
									Date of Birth <span className="text-danger">*</span>
								</label>
								<input
									type="date"
									className="form-control"
									value={birth_dt}
									onChange={({ target }) => setBirthDt(target.value)}
								/>
							</div>
						</div>
						<div className="col-md-6">
							<div className="form-group">
								<label>
									Gender <span className="text-danger">*</span>
								</label>
								<div className="form-group">
									<Select
										options={['F', 'M']}
										value={gender}
										onChange={({ target }) => setGender(target.value)}
									/>
								</div>
							</div>
						</div>
					</div>
					<div
						className={cn('form-group', {
							'has-error':
								isPhoneVerified && phoneData && !phoneData.isTAVCapable,
						})}
					>
						<label className="control-label">
							Mobile Phone (required when no email address)
						</label>
						<MaskedInput
							className="form-control"
							mask={PHONE_MASK}
							value={mobile_phone}
							onChange={({ target }) => setMobilePhone(target.value)}
							onBlur={handleVerifyMobile}
						/>
						{isPhoneVerified && (
							<React.Fragment>
								{phoneData && phoneData.isTAVCapable && (
									<span className="help-block">
										Phone number is Text-a-Visit capable
									</span>
								)}
								{phoneData && !phoneData.isTAVCapable && !phoneData.err && (
									<span className="help-block">
										Phone number is not Text-a-Visit capable
									</span>
								)}
								{phoneData && phoneData.err && (
									<span className="help-block">
										Unable to verify phone number
									</span>
								)}
							</React.Fragment>
						)}
					</div>
					<div className="form-group">
						<label>Email Address (required when no mobile phone)</label>
						<input
							type="email"
							className="form-control"
							value={email_address}
							onChange={({ target }) => setEmailAddress(target.value)}
						/>
					</div>
					<div className="form-group">
						<label>
							Campaigns <span className="text-danger">*</span>
						</label>
						<div className="row">
							{campaigns &&
								campaigns
									.sort((a, b) => (a.name > b.name ? 1 : -1))
									.map((campaign) => (
										<div
											className="col-md-6"
											key={`campaign-${campaign.questionnaire_id}`}
										>
											<Checkbox
												label={campaign.name}
												checked={
													selectedCampaigns[`${campaign.questionnaire_id}`]
												}
												onChange={handleCampaignToggled}
												name="selectedCampaigns"
												value={campaign.questionnaire_id}
											/>
										</div>
									))}
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<div className="pull-right">
						<Button
							title="Cancel"
							large
							onClick={onClose}
							disabled={isAdding || loading}
						/>
						{!campaignPatient && (
							<Button
								className="ml"
								title="Add and Document"
								large
								disabled={!isDocValid || isAdding || loading}
								onClick={() => handleAddPatientToCampaign(false, true)}
								rightIcon="file-o"
							/>
						)}
						<SplitButton
							title={
								campaignPatient && campaignPatient.campaign_demographic_id
									? 'Update and Send'
									: 'Add and Send'
							}
							onClick={() => handleAddPatientToCampaign(true)}
							large
							primary
							containerClassName="ml"
							disabled={!isValid || isAdding || loading}
							items={sendActions}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};

CampaignPatientModal.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	onAdd: PropTypes.func,
	isAdding: PropTypes.bool,
	campaignPatient: PropTypes.object,
	phoneData: PropTypes.object,
	onVerifyMobileNumber: PropTypes.func,
	campaigns: PropTypes.array,
	userId: PropTypes.number,
};

export default CampaignPatientModal;
